/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { phoneMask, phoneDDIMask } from './input-masks';

/** National numbers have 10–11 digits; international add the country prefix. */
const MIN_NATIONAL_DIGITS = 10;
const MAX_MASKABLE_DIGITS = 13;
const MAX_NATIONAL_DIGITS = 11;

/**
 * Whether a phone string carries a country code (leading `+` or more digits
 * than a national number).
 *
 * @param trimmed - The whitespace-trimmed raw value.
 * @param digits - The digit-only extraction of that value.
 * @returns True when the number should be masked with the DDI mask.
 */
function hasCountryCode(trimmed: string, digits: string): boolean {
  return trimmed.startsWith('+') || digits.length > MAX_NATIONAL_DIGITS;
}

/**
 * Whether the digit count falls in the maskable range (10–13).
 *
 * @param digits - The digit-only extraction.
 * @returns True when the number can be formatted.
 */
function isMaskableLength(digits: string): boolean {
  return digits.length >= MIN_NATIONAL_DIGITS && digits.length <= MAX_MASKABLE_DIGITS;
}

/**
 * Formats a stored phone number for display.
 *
 * Reuses the same masks the phone inputs apply, so a number reads identically
 * whether it is being typed or merely shown. Numbers carrying a country code
 * (a leading `+`, or more digits than a national number holds) keep it.
 *
 * Anything that is not a recognizable national or international number is
 * returned untouched rather than mangled into a wrong-looking mask.
 *
 * @param raw - The stored phone value, in any shape.
 * @returns The masked number, or the trimmed input when it cannot be masked.
 */
export function formatPhoneDisplay(raw?: string | null): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D+/g, '');
  if (!isMaskableLength(digits)) return trimmed;

  return hasCountryCode(trimmed, digits) ? phoneDDIMask(digits).masked : phoneMask(digits).masked;
}
